'use client'

import { useEffect, createContext, useContext, useState } from 'react'
import type { PostHog } from 'posthog-js'

/* ------------------------------------------------------------------
   Singleton PostHog instance (lazy-loaded, browser-only)
   ------------------------------------------------------------------ */

let posthogInstance: PostHog | null = null

function getPostHog(): PostHog | null {
  return posthogInstance
}

/* ------------------------------------------------------------------
   Context so children can access the instance via hook
   ------------------------------------------------------------------ */

const PostHogContext = createContext<PostHog | null>(null)

/**
 * Returns the PostHog instance (or null if not configured).
 * Use for custom event capture: `const posthog = usePostHog()`
 */
export function usePostHog(): PostHog | null {
  return useContext(PostHogContext)
}

/* ------------------------------------------------------------------
   Provider Component
   ------------------------------------------------------------------ */

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const [ph, setPh] = useState<PostHog | null>(null)

  useEffect(() => {
    // Only initialize in the browser
    if (typeof window === 'undefined') return

    const key = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const host = process.env.NEXT_PUBLIC_POSTHOG_HOST

    // Graceful fallback: if env vars are missing, skip initialization
    if (!key || !host) return

    // Already initialized (e.g. HMR / StrictMode double-mount)
    if (posthogInstance) {
      setPh(posthogInstance)
      return
    }

    // Dynamic import to avoid SSR bundling issues
    import('posthog-js').then((posthogModule) => {
      const posthog = posthogModule.default

      posthog.init(key, {
        api_host: host,
        capture_pageview: true,
        capture_pageleave: true,
        autocapture: true,
        session_recording: { recordCrossOriginIframes: false },
        persistence: 'localStorage',
        loaded: (ph) => {
          if (process.env.NODE_ENV === 'development') {
            ph.opt_out_capturing()
          }
        },
      })

      posthogInstance = posthog
      setPh(posthog)
    })
  }, [])

  return (
    <PostHogContext.Provider value={ph}>
      {children}
    </PostHogContext.Provider>
  )
}

export { getPostHog }
